import classNames from 'classnames';
import React, { Ref, forwardRef, useId } from 'react';

import { Icon } from '@eika/icon';
import '@eika/input-style';
import { ErrorTag } from '@eika/tag';

import InputLabel from './InputLabel';
import type { InputProps, InputPropsWithRequiredId } from './types';

const Input = forwardRef(
    (
        {
            label,
            sublabel,
            type = 'text',
            error,
            className,
            infoButton,
            id,
            hasExternalLabel = false,
            icon,
            errorIcon,
            children,
            nestedClassNames,
            ...rest
        }: InputProps | InputPropsWithRequiredId,
        ref: Ref<HTMLInputElement>,
    ): React.JSX.Element => {
        const generatedId = useId();
        const targetID = id || generatedId;

        const externalDescribedBy = rest['aria-describedby'] || '';

        return (
            <div
                className={classNames('sgw-input__input-wrapper', className, {
                    'sgw-input__input-wrapper--has-icon': !!icon,
                })}
            >
                {!hasExternalLabel && (
                    <InputLabel
                        htmlFor={targetID}
                        label={label}
                        sublabel={sublabel}
                        infoButton={infoButton}
                        className={nestedClassNames?.label}
                    />
                )}
                <div className={classNames('sgw-input__input-container', nestedClassNames?.inputWrapper)}>
                    {icon && <Icon className="sgw-input__icon" type={icon} />}
                    <input
                        {...rest}
                        className={classNames(
                            'sgw-input__input',
                            { 'sgw-input__error': !!error },
                            nestedClassNames?.input,
                        )}
                        aria-invalid={!!error}
                        type={type}
                        ref={ref}
                        id={targetID}
                        aria-describedby={error ? `${targetID}-error` : externalDescribedBy}
                    />
                    {children}
                    <ErrorTag id={`${targetID}-error`} className="sgw-input__error-label" icon={errorIcon}>
                        {error}
                    </ErrorTag>
                </div>
            </div>
        );
    },
);

export default Input;
